import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import DraftAnalysisPanel from "@/components/DraftAnalysisPanel";
import {
  Briefcase, FileDown, CheckCircle2, DollarSign, Clock,
  Save, Send, History, Users, X, Edit3, MapPin, RefreshCw, Printer, Stamp,
  TrendingUp, AlertTriangle,
} from "lucide-react";
import { fmtDate, fmtTime } from "@/lib/tz";

/**
 * Checkmate Agency Billing Suite — AI Billing Concierge.
 *
 * Groups approved timesheets by Cost Center (Worksite), lets the admin
 * review, edit, and approve invoicing packets, then generates a master
 * Client Billing Report PDF per Cost Center. Every edit stays isolated
 * from weekly payroll unless the admin explicitly ticks "Sync to Payroll".
 *
 * NOTE: Ships as an inline section of the AI Office Manager page — the
 * outer <BillingConcierge/> wrapper simply adds page padding for the
 * (now retired) standalone route; <BillingConciergeSection/> is the
 * embeddable body used inside WorkflowSettings.jsx tabs.
 */

function defaultRange() {
  // Default matches the Company payroll cadence: previous full pay period.
  // Snapped to a locked window so a Client Billing report matches the pay
  // stub it partners with. Admins can override via "Unlock custom range".
  const end = new Date();
  end.setDate(end.getDate() - end.getDay());  // last Saturday (Sun-start weeks)
  const start = new Date(end);
  start.setDate(end.getDate() - 13);           // 14-day biweekly window
  const iso = (d) => d.toISOString().slice(0, 10);
  return { start: iso(start), end: iso(end) };
}

const WEEK_DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// Clinical window: the most recent COMPLETE period of `days` length that ends
// the day before the configured week-start day.
function snapRange(days, weekStartDay) {
  const iso = (d) => d.toISOString().slice(0, 10);
  const end = new Date();
  const back = (end.getUTCDay() - weekStartDay + 7) % 7 || 7;
  end.setUTCDate(end.getUTCDate() - back);          // last day of the closed window
  const start = new Date(end);
  start.setUTCDate(end.getUTCDate() - (days - 1));
  return { start: iso(start), end: iso(end) };
}

function fmtMoney(n) {
  return `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtHours(n) {
  return `${Number(n || 0).toFixed(2)}h`;
}

async function downloadBlob(path, filename) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  } catch (e) { toast.error(formatApiError(e)); }
}

// Open the PDF in a new tab so the browser's native print dialog is one
// keystroke away (Ctrl/Cmd+P). Uses a blob URL because the API is
// bearer-guarded — a plain <a target="_blank" href="/api/…"> would 401.
async function printBlob(path) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const w = window.open(url, "_blank", "noopener,noreferrer");
    if (!w) toast.error("Pop-up blocked — allow pop-ups to print");
    // Keep the object URL alive so the new tab can finish loading + printing.
    setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
  } catch (e) { toast.error(formatApiError(e)); }
}


export function BillingConciergeSection() {
  const [range, setRange] = useState(defaultRange);
  const [rangeUnlocked, setRangeUnlocked] = useState(false);
  const [packets, setPackets] = useState([]);
  const [worksites, setWorksites] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // packet key currently generating
  const [rateEdit, setRateEdit] = useState({}); // { [worksite_id]: value }
  const [editEntry, setEditEntry] = useState(null); // { packet, entry }
  const [params, setParams] = useState({ billing_period_mode: "biweekly", billing_week_start_day: 0, overtime_threshold: 40 });
  const [runRate, setRunRate] = useState({});   // { [worksite_id]: per-run rate for THIS report }
  const [analysis, setAnalysis] = useState(null); // { reportId, data } — Draft Analysis panel

  // Weekly Billing Parameters — persisted on the company, and they drive the
  // aggregation window for every rollup on this page.
  useEffect(() => {
    api.get("/billing/settings").then(({ data }) => {
      setParams(data);
      if (data.period_days > 0) {
        setRange(snapRange(data.period_days, data.billing_week_start_day));
        setRangeUnlocked(false);
      } else {
        setRangeUnlocked(true);
      }
    }).catch(() => {});
  }, []);

  const openAnalysis = async (reportId) => {
    setAnalysis({ reportId, data: null });
    try {
      const { data } = await api.get(`/billing/reports/${reportId}/analysis`);
      setAnalysis({ reportId, data });
    } catch (e) { toast.error(formatApiError(e)); setAnalysis(null); }
  };

  const sealReport = async (h) => {
    setBusy(`seal:${h.id}`);
    try {
      await api.post(`/billing/reports/${h.id}/seal`);
      toast.success("Report sealed — ready for client release");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(null);
  };

  const saveParams = async (next) => {
    const merged = { ...params, ...next };
    setParams(merged);
    try {
      const { data } = await api.put("/billing/settings", {
        billing_period_mode: merged.billing_period_mode,
        billing_week_start_day: Number(merged.billing_week_start_day),
      });
      if (data.period_days > 0) {
        setRange(snapRange(data.period_days, Number(merged.billing_week_start_day)));
        setRangeUnlocked(false);
      } else {
        setRangeUnlocked(true);
      }
      toast.success("Weekly billing parameters saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, ws, h] = await Promise.all([
        api.get(`/billing/prep?start=${range.start}&end=${range.end}`),
        api.get("/worksites"),
        api.get("/billing/reports"),
      ]);
      setPackets(p.data.packets || []);
      setWorksites(ws.data || []);
      setHistory(h.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  }, [range.start, range.end]);
  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    const hrs = packets.reduce((s, p) => s + Number(p.total_hours || 0), 0);
    const bill = packets.reduce((s, p) => s + Number(p.total_billable || 0), 0);
    return { hours: hrs, billable: bill, count: packets.length };
  }, [packets]);

  const saveRate = async (ws_id) => {
    const val = Number(rateEdit[ws_id] || 0);
    try {
      await api.put(`/worksites/${ws_id}/billable-rate`, { client_billable_rate: val });
      toast.success(`Client rate saved — ${fmtMoney(val)}/h`);
      setRateEdit((r) => { const c = { ...r }; delete c[ws_id]; return c; });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const approve = async (p) => {
    if (!p.worksite_id) return;
    setBusy(`approve:${p.worksite_id}`);
    try {
      await api.post("/billing/packets/approve", {
        worksite_id: p.worksite_id,
        period_start: range.start, period_end: range.end,
      });
      toast.success(`Packet approved · ${p.worksite_name}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(null);
  };

  const generate = async (p) => {
    if (!p.worksite_id) {
      toast.error("Unassigned entries can't be invoiced — assign a Cost Center first");
      return;
    }
    setBusy(`gen:${p.worksite_id}`);
    try {
      const override = runRate[p.worksite_id];
      const { data } = await api.post("/billing/reports/generate", {
        worksite_id: p.worksite_id,
        period_start: range.start, period_end: range.end,
        ...(override !== undefined && override !== "" ? { billable_rate: Number(override) } : {}),
      });
      toast.success("Draft ready for review — verify, then Seal for the client");
      // Trigger download of the freshly-generated draft
      await downloadBlob(`/billing/reports/${data.report_id}/pdf`,
        `DRAFT-billing-${p.worksite_name.replace(/\s+/g, "-")}-${range.start}-${range.end}.pdf`);
      await load();
      setAnalysis({ reportId: data.report_id, data: null });
      try {
        const { data: a } = await api.get(`/billing/reports/${data.report_id}/analysis`);
        setAnalysis({ reportId: data.report_id, data: a });
      } catch { setAnalysis(null); }
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(null);
  };

  const downloadEmployeeSheet = async (p, emp) => {
    await downloadBlob(
      `/billing/timesheet/${emp.employee_id}/pdf?start=${range.start}&end=${range.end}&worksite_id=${p.worksite_id || "__all__"}`,
      `timesheet-${emp.employee_name.replace(/\s+/g, "-")}-${range.start}-${range.end}.pdf`
    );
  };

  return (
    <div>
      <div className="overline mb-2 flex items-center gap-2">
        <Briefcase className="w-4 h-4" /> § AGENCY BILLING · GROUPED BY COST CENTER
      </div>
      <div className="flex items-baseline justify-between flex-wrap gap-4 mb-6">
        <div>
          <h2 className="font-heading font-black text-3xl tracking-tight" data-testid="billing-concierge-title">Billing Concierge</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Approved timesheets grouped by Cost Center. Review, tweak, and hand off invoicing packets — edits stay
            out of weekly payroll unless you tick <b>Sync to Payroll</b> on a row.
          </p>
        </div>
        <button
          data-testid="billing-refresh"
          onClick={load}
          className="border-2 border-ink px-4 py-2 font-mono text-xs uppercase tracking-widest inline-flex items-center gap-2 hover:bg-secondary"
        ><RefreshCw className="w-4 h-4" /> Refresh</button>
      </div>

      {/* Weekly Billing Parameters — clinical aggregation window */}
      <div className="hard-card p-4 mb-4" data-testid="weekly-billing-params">
        <div className="overline mb-3">§ Weekly Billing Parameters</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
          <label className="block">
            <span className="block font-mono text-[10px] uppercase tracking-widest mb-1">Aggregation window</span>
            <select
              data-testid="billing-period-mode"
              value={params.billing_period_mode}
              onChange={(e) => saveParams({ billing_period_mode: e.target.value })}
              className="border-2 border-ink px-2 py-1.5 font-mono text-xs w-full bg-white"
            >
              <option value="weekly">Weekly · 7-day</option>
              <option value="biweekly">Biweekly · 14-day</option>
              <option value="custom">Custom range</option>
            </select>
          </label>
          <label className="block">
            <span className="block font-mono text-[10px] uppercase tracking-widest mb-1">Week starts on</span>
            <select
              data-testid="billing-week-start"
              value={params.billing_week_start_day}
              disabled={params.billing_period_mode === "custom"}
              onChange={(e) => saveParams({ billing_week_start_day: Number(e.target.value) })}
              className="border-2 border-ink px-2 py-1.5 font-mono text-xs w-full bg-white disabled:bg-secondary/60"
            >
              {WEEK_DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
            </select>
          </label>
          <div className="font-mono text-[11px] text-muted-foreground leading-relaxed" data-testid="billing-params-note">
            Every rollup, packet and report on this page aggregates over this window.
            Weekly overtime threshold in force: <b data-testid="billing-ot-threshold">{Number(params.overtime_threshold || 40).toFixed(2)}h</b>.
          </div>
        </div>
      </div>

      {/* Period picker + rollup KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="hard-card p-4">
          <div className="overline mb-2 flex items-center justify-between gap-2">
            <span>Billing Period {!rangeUnlocked && <span className="text-[9px] px-1 py-0.5 border border-ink bg-secondary normal-case">{params.billing_period_mode === "weekly" ? "locked to 7-day window" : "locked to pay period"}</span>}</span>
            <label className="inline-flex items-center gap-1 cursor-pointer normal-case font-normal">
              <input
                type="checkbox"
                data-testid="unlock-custom-range"
                checked={rangeUnlocked}
                onChange={(e) => setRangeUnlocked(e.target.checked)}
                className="w-3 h-3 accent-ink"
              />
              <span className="text-[9px] tracking-normal">Unlock custom range</span>
            </label>
          </div>
          <div className="flex gap-2 items-center">
            <input
              type="date" data-testid="billing-start"
              value={range.start}
              disabled={!rangeUnlocked}
              onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
              className="border-2 border-ink px-2 py-1 font-mono text-xs flex-1 disabled:bg-secondary/60 disabled:cursor-not-allowed"
            />
            <span className="font-mono text-xs">→</span>
            <input
              type="date" data-testid="billing-end"
              value={range.end}
              disabled={!rangeUnlocked}
              onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
              className="border-2 border-ink px-2 py-1 font-mono text-xs flex-1 disabled:bg-secondary/60 disabled:cursor-not-allowed"
            />
          </div>
        </div>
        <KPI icon={Briefcase} label="Invoicing Packets" value={totals.count} testid="kpi-packets" />
        <KPI icon={Clock} label="Total Hours" value={fmtHours(totals.hours)} testid="kpi-hours" />
        <KPI icon={DollarSign} label="Total Billable" value={fmtMoney(totals.billable)} testid="kpi-billable" highlight />
      </div>

      {loading && <div className="hard-card p-6 font-mono text-xs text-muted-foreground">Loading packets…</div>}

      {!loading && packets.length === 0 && (
        <div className="hard-card p-8 font-mono text-xs text-muted-foreground text-center" data-testid="billing-empty">
          No approved timesheets found in this window. Try widening the date range or approving pending timesheets in <b>Approvals</b>.
        </div>
      )}

      {!loading && packets.map((p) => {
        const pending = p.status !== "invoiced";
        const localRate = rateEdit[p.worksite_id];
        return (
          <div key={p.worksite_id || "unassigned"}
               className="hard-card p-6 mb-6"
               data-testid={`packet-${p.worksite_id || "unassigned"}`}>
            <div className="flex items-start justify-between flex-wrap gap-4 mb-4 pb-4 border-b-2 border-ink">
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="p-2 border-2 border-ink bg-ink text-white"><MapPin className="w-4 h-4" /></div>
                  <h2 className="font-heading font-black text-2xl">{p.worksite_name}</h2>
                  <StatusPill status={p.status} />
                </div>
                {p.worksite_address && <div className="font-mono text-[11px] text-muted-foreground mt-2 ml-1">{p.worksite_address}</div>}
              </div>
              <div className="text-right">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Total Billable</div>
                <div className="font-heading font-black text-3xl" data-testid={`packet-total-${p.worksite_id || "unassigned"}`}>{fmtMoney(p.total_billable)}</div>
                <div className="font-mono text-xs text-muted-foreground">{fmtHours(p.total_hours)} × {fmtMoney(p.billable_rate)}/h</div>
              </div>
            </div>

            {/* Client Billable Rate editor */}
            {p.worksite_id && (
              <div className="mb-4 flex items-center gap-3 flex-wrap">
                <div className="overline">Client Billable Rate</div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm">$</span>
                  <input
                    type="number" step="0.01" min="0"
                    data-testid={`rate-input-${p.worksite_id}`}
                    value={localRate !== undefined ? localRate : (p.billable_rate || 0)}
                    onChange={(e) => setRateEdit((r) => ({ ...r, [p.worksite_id]: e.target.value }))}
                    className="border-2 border-ink px-2 py-1 font-mono text-sm w-24"
                  />
                  <span className="font-mono text-xs text-muted-foreground">/hour</span>
                  {localRate !== undefined && Number(localRate) !== Number(p.billable_rate || 0) && (
                    <button
                      data-testid={`save-rate-${p.worksite_id}`}
                      onClick={() => saveRate(p.worksite_id)}
                      className="hard-btn px-3 py-1 font-mono text-[10px] uppercase tracking-widest inline-flex items-center gap-1"
                    ><Save className="w-3 h-3" /> Save Rate</button>
                  )}
                </div>
              </div>
            )}

            {/* Employees table */}
            <div className="overflow-x-auto">
              <table className="w-full font-mono text-sm">
                <thead className="bg-secondary">
                  <tr>
                    <th className="text-left px-3 py-2 uppercase text-[10px] tracking-widest">Employee</th>
                    <th className="text-right px-3 py-2 uppercase text-[10px] tracking-widest">Hours</th>
                    <th className="text-right px-3 py-2 uppercase text-[10px] tracking-widest">Billable</th>
                    <th className="text-left px-3 py-2 uppercase text-[10px] tracking-widest">Entries</th>
                    <th className="text-right px-3 py-2 uppercase text-[10px] tracking-widest">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {p.employees.map((emp) => (
                    <tr key={emp.employee_id} className="border-t border-ink/20" data-testid={`packet-emp-${emp.employee_id}`}>
                      <td className="px-3 py-2 font-semibold">{emp.employee_name}</td>
                      <td className="px-3 py-2 text-right">{fmtHours(emp.hours)}</td>
                      <td className="px-3 py-2 text-right">{fmtMoney(emp.billable)}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {emp.entries.length} clock-in{emp.entries.length !== 1 ? "s" : ""}
                        {emp.entries.some((e) => e._edited) && (
                          <span className="ml-2 px-1 py-0.5 border border-warn bg-warn/10 text-[9px]">edited</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap space-x-2">
                        <button
                          data-testid={`edit-entries-${emp.employee_id}`}
                          onClick={() => setEditEntry({ packet: p, employee: emp })}
                          className="border-2 border-ink px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-ink hover:text-white inline-flex items-center gap-1"
                        ><Edit3 className="w-3 h-3" /> Edit</button>
                        <button
                          data-testid={`dl-timesheet-${emp.employee_id}`}
                          onClick={() => downloadEmployeeSheet(p, emp)}
                          className="border-2 border-ink px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-secondary inline-flex items-center gap-1"
                        ><FileDown className="w-3 h-3" /> PDF</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Actions */}
            {p.worksite_id && (
              <div className="mt-5 flex flex-wrap items-center gap-3 justify-end">
                {p.status === "draft" && (
                  <button
                    data-testid={`approve-${p.worksite_id}`}
                    disabled={busy === `approve:${p.worksite_id}`}
                    onClick={() => approve(p)}
                    className="border-2 border-ink px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-secondary inline-flex items-center gap-2 disabled:opacity-40"
                  ><CheckCircle2 className="w-4 h-4" /> {busy === `approve:${p.worksite_id}` ? "Approving…" : "Approve Packet"}</button>
                )}
                <label className="inline-flex items-center gap-2 border-2 border-ink px-3 py-1.5">
                  <span className="font-mono text-[10px] uppercase tracking-widest">Rate this run $</span>
                  <input
                    data-testid={`run-rate-${p.worksite_id}`}
                    type="number" step="0.01" min="0"
                    placeholder={String(p.billable_rate || 0)}
                    value={runRate[p.worksite_id] ?? ""}
                    onChange={(e) => setRunRate((r) => ({ ...r, [p.worksite_id]: e.target.value }))}
                    className="w-20 font-mono text-xs border-b-2 border-ink/30 focus:border-ink outline-none"
                  />
                  <span className="font-mono text-[10px] text-muted-foreground">/h · this report only</span>
                </label>
                <button
                  data-testid={`generate-${p.worksite_id}`}
                  disabled={busy === `gen:${p.worksite_id}` || (p.billable_rate || 0) === 0}
                  onClick={() => generate(p)}
                  title={(p.billable_rate || 0) === 0 ? "Set a Client Billable Rate first" : "Generate master PDF"}
                  className="hard-btn px-4 py-2 font-mono text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-40"
                ><Send className="w-4 h-4" /> {busy === `gen:${p.worksite_id}` ? "Generating…" : "Generate Draft Report"}</button>
              </div>
            )}
          </div>
        );
      })}

      {/* Billing History */}
      <div className="mt-10">
        <div className="flex items-center gap-2 mb-4">
          <History className="w-5 h-5" />
          <h2 className="font-heading font-black text-2xl">Billing History</h2>
        </div>
        {history.length === 0 ? (
          <div className="hard-card p-6 font-mono text-xs text-muted-foreground" data-testid="billing-history-empty">
            No client billing reports generated yet. Approve a packet above and hit <b>Generate Client Billing Report</b> to create one.
          </div>
        ) : (
          <div className="hard-card overflow-x-auto">
            <table className="w-full font-mono text-sm" data-testid="billing-history-table">
              <thead className="bg-ink text-white">
                <tr>
                  <th className="text-left px-3 py-2 uppercase text-[10px] tracking-widest">Cost Center</th>
                  <th className="text-left px-3 py-2 uppercase text-[10px] tracking-widest">Period</th>
                  <th className="text-right px-3 py-2 uppercase text-[10px] tracking-widest">Hours</th>
                  <th className="text-right px-3 py-2 uppercase text-[10px] tracking-widest">Total</th>
                  <th className="text-left px-3 py-2 uppercase text-[10px] tracking-widest">Status</th>
                  <th className="text-left px-3 py-2 uppercase text-[10px] tracking-widest">Generated</th>
                  <th className="text-right px-3 py-2 uppercase text-[10px] tracking-widest">PDF</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} className="border-t border-ink/20" data-testid={`history-row-${h.id}`}>
                    <td className="px-3 py-2 font-semibold">{h.worksite_name}</td>
                    <td className="px-3 py-2">{h.period_start} → {h.period_end}</td>
                    <td className="px-3 py-2 text-right">{fmtHours(h.total_hours)}</td>
                    <td className="px-3 py-2 text-right font-bold">{fmtMoney(h.total_billable)}</td>
                    <td className="px-3 py-2">
                      <span
                        data-testid={`history-status-${h.id}`}
                        className={"inline-block border-2 border-ink px-2 py-0.5 text-[9px] uppercase tracking-widest " +
                          (h.status === "sealed" ? "bg-ink text-white" : "bg-warn")}
                      >{h.status === "sealed" ? "Sealed" : "Draft Review"}</span>
                      {h.status === "sealed" && h.sealed_at && (
                        <div className="text-[10px] text-muted-foreground mt-1">
                          {new Date(h.sealed_at).toLocaleString()}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {new Date(h.generated_at).toLocaleString()}
                      {h.generated_by_name && <div>by {h.generated_by_name}</div>}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap space-x-2">
                      {h.status === "draft_review" && (
                        <button
                          data-testid={`history-analysis-${h.id}`}
                          onClick={() => openAnalysis(h.id)}
                          title="Draft Analysis — billed vs paid spread (admin only)"
                          className="border-2 border-ink px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-secondary inline-flex items-center gap-1"
                        ><TrendingUp className="w-3 h-3" /> Analysis</button>
                      )}
                      {h.status === "draft_review" && (
                        <button
                          data-testid={`history-seal-${h.id}`}
                          disabled={busy === `seal:${h.id}`}
                          onClick={() => sealReport(h)}
                          title="Seal this report for client release"
                          className="hard-btn px-3 py-1 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 disabled:opacity-40"
                        ><Stamp className="w-3 h-3" /> {busy === `seal:${h.id}` ? "Sealing…" : "Seal"}</button>
                      )}
                      <button
                        data-testid={`history-print-${h.id}`}
                        title="Open in new tab for printing"
                        onClick={() => printBlob(`/billing/reports/${h.id}/pdf`)}
                        className="border-2 border-ink px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-secondary inline-flex items-center gap-1"
                      ><Printer className="w-3 h-3" /> Print</button>
                      <button
                        data-testid={`history-dl-${h.id}`}
                        onClick={() => downloadBlob(`/billing/reports/${h.id}/pdf`, `billing-${(h.worksite_name || "client").replace(/\s+/g, "-")}-${h.period_start}-${h.period_end}.pdf`)}
                        className="border-2 border-ink px-3 py-1 text-[10px] uppercase tracking-widest hover:bg-ink hover:text-white inline-flex items-center gap-1"
                      ><FileDown className="w-3 h-3" /> Download</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <DraftAnalysisPanel state={analysis} onClose={() => setAnalysis(null)} />

      {editEntry && (
        <EditEntryModal
          packet={editEntry.packet}
          employee={editEntry.employee}
          worksites={worksites}
          onClose={() => setEditEntry(null)}
          onSaved={async () => { setEditEntry(null); await load(); }}
        />
      )}
    </div>
  );
}

function KPI({ icon: Icon, label, value, highlight, testid }) {
  return (
    <div className={`hard-card p-4 ${highlight ? "bg-good/10 border-good" : ""}`} data-testid={testid}>
      <div className="overline mb-2 flex items-center gap-2"><Icon className="w-4 h-4" /> {label}</div>
      <div className={`font-heading font-black ${highlight ? "text-3xl" : "text-2xl"}`}>{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    draft: { text: "Draft", cls: "border-ink bg-secondary" },
    approved: { text: "Approved", cls: "border-good bg-good/20" },
    invoiced: { text: "Invoiced", cls: "border-ink bg-ink text-white" },
  };
  const m = map[status] || map.draft;
  return <span className={`px-2 py-1 border-2 ${m.cls} font-mono text-[10px] uppercase tracking-widest`}>{m.text}</span>;
}

function EditEntryModal({ packet, employee, worksites, onClose, onSaved }) {
  const [rows, setRows] = useState(() =>
    employee.entries.map((e) => ({
      id: e.id || e._id,
      original_hours: Number(e.duration_hours || 0),
      hours: Number(e.duration_hours || 0),
      worksite_id: e.worksite_id || packet.worksite_id || "",
      original_worksite_id: e.worksite_id || packet.worksite_id || "",
      clock_in_at: e.clock_in_at,
      clock_out_at: e.clock_out_at,
      note: e._edit_note || "",
      sync_to_payroll: false,
      synced_before: !!e._synced_to_payroll,
    }))
  );
  const [saving, setSaving] = useState(false);

  const changed = rows.some((r) =>
    Number(r.hours) !== Number(r.original_hours) ||
    r.worksite_id !== r.original_worksite_id ||
    (r.note || "") !== ""
  );

  const save = async () => {
    setSaving(true);
    try {
      const dirty = rows.filter((r) =>
        Number(r.hours) !== Number(r.original_hours) ||
        r.worksite_id !== r.original_worksite_id ||
        (r.note || "").trim().length > 0 ||
        r.sync_to_payroll
      );
      for (const r of dirty) {
        await api.post("/billing/packets/edit", {
          time_entry_id: r.id,
          hours_override: Number(r.hours),
          worksite_override_id: r.worksite_id || null,
          note: r.note || "",
          sync_to_payroll: !!r.sync_to_payroll,
        });
      }
      toast.success(`${dirty.length} edit${dirty.length === 1 ? "" : "s"} saved`);
      await onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-ink/50 z-50 flex items-center justify-center p-4" data-testid="edit-entries-modal">
      <div className="bg-white border-2 border-ink shadow-hard-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b-2 border-ink p-4 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="overline">§ EDIT BILLING ENTRIES</div>
            <h2 className="font-heading font-black text-2xl">{employee.employee_name}</h2>
            <div className="font-mono text-xs text-muted-foreground">{packet.worksite_name} · {packet.period_start} → {packet.period_end}</div>
          </div>
          <button onClick={onClose} className="p-2 border-2 border-ink hover:bg-ink hover:text-white" data-testid="close-edit-modal"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4 overflow-x-auto">
          <div className="mb-4 hard-card p-3 font-mono text-[11px] bg-warn/10 border-warn">
            <b>Sync Safeguard:</b> Edits here update the invoicing packet only. Tick <b>Sync to Payroll</b> on a
            row to also push the change back into that shift's original timesheet.
          </div>

          <table className="w-full font-mono text-xs">
            <thead className="bg-secondary">
              <tr>
                <th className="text-left px-2 py-2 uppercase text-[10px] tracking-widest">Date · Clock</th>
                <th className="text-right px-2 py-2 uppercase text-[10px] tracking-widest">Hours</th>
                <th className="text-left px-2 py-2 uppercase text-[10px] tracking-widest">Cost Center</th>
                <th className="text-left px-2 py-2 uppercase text-[10px] tracking-widest">Note</th>
                <th className="text-center px-2 py-2 uppercase text-[10px] tracking-widest">Sync to Payroll</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                return (
                  <tr key={r.id} className="border-t border-ink/20" data-testid={`edit-row-${r.id}`}>
                    <td className="px-2 py-2 whitespace-nowrap">
                      <div>{fmtDate(r.clock_in_at)}</div>
                      <div className="text-[10px] text-muted-foreground">{fmtTime(r.clock_in_at)} → {r.clock_out_at ? fmtTime(r.clock_out_at) : "—"}</div>
                    </td>
                    <td className="px-2 py-2 text-right">
                      <input
                        type="number" step="0.01" min="0"
                        data-testid={`edit-hours-${r.id}`}
                        value={r.hours}
                        onChange={(e) => setRows((rr) => rr.map((x, idx) => idx === i ? { ...x, hours: e.target.value } : x))}
                        className="border-2 border-ink px-2 py-1 w-20 text-right font-mono text-xs"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <select
                        data-testid={`edit-ws-${r.id}`}
                        value={r.worksite_id || ""}
                        onChange={(e) => setRows((rr) => rr.map((x, idx) => idx === i ? { ...x, worksite_id: e.target.value } : x))}
                        className="border-2 border-ink px-2 py-1 font-mono text-xs"
                      >
                        <option value="">— none —</option>
                        {worksites.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-2">
                      <input
                        type="text" placeholder="reason for edit…"
                        data-testid={`edit-note-${r.id}`}
                        value={r.note}
                        onChange={(e) => setRows((rr) => rr.map((x, idx) => idx === i ? { ...x, note: e.target.value } : x))}
                        className="border-2 border-ink px-2 py-1 w-full font-mono text-xs"
                      />
                    </td>
                    <td className="px-2 py-2 text-center">
                      <input
                        type="checkbox"
                        data-testid={`edit-sync-${r.id}`}
                        checked={r.sync_to_payroll}
                        onChange={(e) => setRows((rr) => rr.map((x, idx) => idx === i ? { ...x, sync_to_payroll: e.target.checked } : x))}
                        className="w-5 h-5 accent-ink"
                      />
                      {r.synced_before && <div className="text-[9px] text-muted-foreground">previously synced</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="border-t-2 border-ink p-4 flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="border-2 border-ink px-4 py-2 font-mono text-xs uppercase tracking-widest">Cancel</button>
          <button
            data-testid="save-edits"
            disabled={!changed || saving}
            onClick={save}
            className="hard-btn px-4 py-2 font-mono text-xs uppercase tracking-widest disabled:opacity-40 inline-flex items-center gap-2"
          ><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Edits"}</button>
        </div>
      </div>
    </div>
  );
}
